import { cloneDeep } from "lodash";
import {
  Dynamic,
  FieldValues,
  FieldSchema,
  DynamicPropertyType,
  FieldName,
  isEmpty,
  EMPTY_VALUE,
  prepareDynamicPropertyValue,
} from "../Schema";
import {
  Mapping,
  DynamicManager, CustomState, BasicState
} from "../common/DynamicManager";
import { DataStringSchema, toCalendarDay } from "../data/DataManager";
import { formatDateCell } from "../csv/translators";


export type ErrorMessage = string | typeof EMPTY_VALUE;

export interface ValidationBasicSchema {
  required?: boolean;
  custom?: ErrorMessage;
}

export interface ValidationStringSchema extends ValidationBasicSchema {
  regex?: RegExp;
}

export interface ValidationDateSchema extends ValidationBasicSchema {
  minDate?: Date;
  maxDate?: Date;
}

export type ValidationSchema = ValidationBasicSchema |
  ValidationStringSchema |
  ValidationDateSchema;

export interface ValidationFieldStatus {
  valid: boolean;
  errorMessages: string[];
}

export const defaultValidationFieldStatus: ValidationFieldStatus = {
  valid: true,
  errorMessages: [],
};

export interface ValidationFieldsStatus {
  [key: string]: ValidationFieldStatus;
}

export type ValidationFieldState = BasicState  & {
  [key: string]: ErrorMessage;
}

export interface ValidationState extends CustomState {
  valid: boolean;
  errorMessages: Record<string, ErrorMessage[]>;
}

export const defaultValidationState: ValidationState = {
  loading: true,
  valid: true,
  errorMessages: {},
};

/**
 * Manager for handling validation-related state and logic based on a given schema.
 * It extends the SchemaManager to use its state management capabilities
 * while adding specific logic for requirements and handling data types and variants.
 */
export class ValidationManager extends DynamicManager<ValidationFieldState, ValidationState> {
  /**
   * Custom state summarizing the validation status of all fields,
   * including processing status. Mostly used for providing an overall
   * validation status and error messages.
   */
  customState: ValidationState = defaultValidationState;

  /**
   * Updates the custom state of the ValidationManager based on the current field states.
   * It sets the processing status, overall validity, and gathers all error messages.
   * This method should be called after any field state update to ensure the custom state
   * reflects the current validation status of all fields.
   *
   * @returns A promise that resolves when the custom state has been updated.
   */
  updateCustomState()  {
    let valid = true;
    const errorMessages = Object.fromEntries(
      Object.entries(this.state)
        .reduce(
          (messages, [fieldName, fieldState]) => {
            const fieldErrorMessages = Object.values(fieldState)
              .filter(Boolean) as string[];
            if (fieldErrorMessages.length) {
              valid = false;
              messages.push([fieldName, fieldErrorMessages]);
            }
            return messages;
          },
          [] as [string, string[]][]
        )
    );
    this.customState = {
      loading: this.loading,
      valid,
      errorMessages,
    }
  }

  /**
   * Prepares a dynamic error message generator for required fields based on the field schema.
   *
   * @param field - schema of the field to prepare the error message for.
   * @returns A Dynamic Structure that generates an error message if the field
   *          is required and empty, or null if the field is not required or has a value.
   */
  prepareRequiredErrorMessage(field: FieldSchema): DynamicPropertyType<ErrorMessage> {
    if (field.validation?.required) {
      const preparedRequired = prepareDynamicPropertyValue(
        field.validation.required
      );
      const preparedActive = prepareDynamicPropertyValue(
        field.data?.active ?? true
      );
      return {
        getter: async (values) => {
          if (isEmpty(values[field.name])) {
            const active = await preparedActive.getter(values);
            const required = await preparedRequired.getter(values);
            return (active && required) ?
              "This field is required" :
              EMPTY_VALUE;
          }
          return EMPTY_VALUE;
        },
        fields: Array.from(new Set([
          field.name,
          ...preparedRequired.fields,
          ...preparedActive.fields,
        ])),
      }
    }
    return EMPTY_VALUE;
  }

  /**
   * Prepares the error message for a field that declares a maximum length.
   *
   * The limit lives on the `data` aspect, because it describes the value rather than the
   * check — the form reads the same number to cap its input. Enforcing it here as well is
   * what makes it a rule rather than a hint: a form input stops at the limit, but a value
   * arriving from a CSV, a bulk edit or the API never passed through that input.
   *
   * Measured on the value's text, so a number carrying more digits than the field allows is
   * caught alongside an over-long string.
   *
   * @param field - schema of the field to prepare the error message for.
   * @returns A getter for the message, or nothing when the field declares no limit.
   */
  prepareMaxLengthErrorMessage(field: FieldSchema): DynamicPropertyType<ErrorMessage> {
    // Declared on DataStringSchema alone, so it is read off the union rather than through it.
    const maxLength = (field.data as DataStringSchema | undefined)?.maxLength;
    if (typeof maxLength !== "number") {
      return EMPTY_VALUE;
    }
    return {
      getter: (values) => {
        const value = values[field.name];
        if (isEmpty(value) || typeof value === "object") {
          return EMPTY_VALUE;
        }
        return String(value).length > maxLength ?
          `This field accepts at most ${maxLength} characters` :
          EMPTY_VALUE;
      },
      fields: [field.name],
    };
  }

  /**
   * Prepares the error message for a field that declares a `minDate` or a `maxDate`.
   *
   * The bounds have always been declared on the `validation` aspect, but their only reader was
   * the form, which hands them to a date picker as its selectable range. That makes them a
   * property of one input rather than a rule — for the same reason `maxLength` is enforced
   * here as well as capping the input: a value arriving from a CSV, a bulk edit or the API
   * never passed through that picker.
   *
   * Both bounds are read dynamically, so a field may declare one as a getter — over the
   * current date, or over another field's value.
   *
   * @param field - schema of the field to prepare the error message for.
   * @returns A getter for the message, or nothing when the field declares no bound.
   */
  prepareDateRangeErrorMessage(field: FieldSchema): DynamicPropertyType<ErrorMessage> {
    // Declared on ValidationDateSchema alone, so they are read off the union rather than
    // through it. `Dynamic` is what the aspect actually holds: either bound may be a getter.
    const { minDate, maxDate } = (field.validation ?? {}) as Dynamic<ValidationDateSchema>;
    if (minDate === undefined && maxDate === undefined) {
      return EMPTY_VALUE;
    }
    const preparedMin = prepareDynamicPropertyValue(minDate);
    const preparedMax = prepareDynamicPropertyValue(maxDate);
    return {
      getter: async (values) => {
        // Anything that is not a date is not this rule's business. An empty field is the
        // `required` rule's, and text that was meant to be a date but could not be read is
        // reported by `CsvManager.findUnparseableValues` — naming the same bad cell twice
        // would only make a failure report harder to read.
        const day = toCalendarDay(values[field.name]);
        if (!day) {
          return EMPTY_VALUE;
        }
        const earliest = toCalendarDay(await preparedMin.getter(values));
        if (earliest && day < earliest) {
          return `This field cannot be earlier than ${formatDateCell(earliest)}`;
        }
        const latest = toCalendarDay(await preparedMax.getter(values));
        if (latest && day > latest) {
          return `This field cannot be later than ${formatDateCell(latest)}`;
        }
        return EMPTY_VALUE;
      },
      fields: Array.from(new Set([
        field.name,
        ...preparedMin.fields,
        ...preparedMax.fields,
      ])),
    };
  }

  /**
   * Generates a mapping for the validation field state based on the field schema.
   *
   * @param field - schema of the field to generate the mapping for.
   * @returns A mapping for the validation field state, including error
   *          message generators for required fields.
   */
  getMapping(field: FieldSchema): Mapping<ValidationFieldState> {
    return {
      required: this.prepareRequiredErrorMessage(field),
      maxLength: this.prepareMaxLengthErrorMessage(field),
      dateRange: this.prepareDateRangeErrorMessage(field),
      custom: prepareDynamicPropertyValue(
        (field.validation as ValidationBasicSchema)?.custom || EMPTY_VALUE
      ),
    };
  }

  /**
   * Validates a specific field based on its name and the current field values.
   * It updates the field state and returns the validation status for that field.
   * If alreadyProcessing is true, it sets the processing status before and after validation.
   * It also triggers the validation of other fields that are connected through
   * subscriptions to ensure that all dependent validations are updated accordingly.
   *
   * @param name - name of the field to validate.
   * @param values - current values of all fields to validate against.
   * @param alreadyProcessing - flag indicating if the processing status has already
   *                            been set before calling this method.
   * @param skipSubscriptions - flag indicating whether to skip triggering validation
   *                            of subscribed fields.
   * @returns A promise that resolves after validation is done.
   */
  public async validateField(
    name: FieldName,
    values: FieldValues,
    alreadyProcessing: boolean = false,
    skipSubscriptions: boolean = false
  ) {
    if (!alreadyProcessing) {
      await this.setLoading(true);
    }
    await this.updateFieldState(
      name,
      values,
      [],
      skipSubscriptions
    );
    if (!alreadyProcessing) {
      await this.setLoading(false);
    }
  }

  /**
   * Validates all fields in the schema and returns their validation status.
   * It triggers the validation of other fields that are connected through
   * subscriptions to ensure that all dependent validations are updated accordingly.
   *
   * @param values - current values of all fields to validate against.
   * @returns A promise that resolves with the validation status of all fields.
   */
  public async validateAllFields(
    values: FieldValues
  ): Promise<ValidationFieldsStatus> {
    await this.setLoading(true);
    const validationStatus = Object.fromEntries(
      await Promise.all(
        Object.keys(this.schema).map(
          async (fieldName) => {
            return [
              fieldName,
              await this.validateField(
                fieldName,
                values,
                true,
                true
              )
            ];
          }
        )
      )
    );
    await this.setLoading(false);
    return cloneDeep({...validationStatus});
  }

  /**
   * Retrieves the validation status of a specific field based on its name.
   * Status is different from the state as it provides a more user-friendly format
   * of validation status, including an array of error messages and an overall
   * validity flag, while the state contains specific only error messages.
   *
   * @param name - name of the field to retrieve the validation status for.
   * @returns The validation status of the specified field, including error messages
   *          and validity flag.
   */
  public getFieldValidationStatus(
    name: FieldName
  ): ValidationFieldStatus {
    const errorMessages = Object.values(
      this.getFieldState(name) || {}
    ).filter(Boolean) as string[];

    return cloneDeep({
      errorMessages,
      valid: errorMessages.length === 0
    });
  }

  /**
   * Retrieves the overall validation state, including processing status, overall validity,
   * and all error messages from all fields. This state is useful for providing a summary
   * of the validation status of the entire form or schema.
   *
   * @returns The overall validation state, including processing status, overall validity,
   *          and all error messages from all fields.
   */
  public getValidationState(): ValidationState {
    return cloneDeep(this.customState);
  }
}
